import localStorageService from "./localStorageService";
import { noteSessionSaved, showRareEffort } from "../utils/badgeBus";

const API_URL = process.env.REACT_APP_API_URL || "/api";

/** Full URL for an API path, for callers that fetch outside apiRequest. */
export const apiUrl = (endpoint) => `${API_URL}${endpoint}`;
const AUTH_TOKEN_KEY = "auth_token";
const AUTH_STORAGE_TYPES = ["localStorage", "sessionStorage"];
const NETWORK_ERROR = "network_unavailable";

/**
 * True when the request never reached the server — offline, DNS, TLS, proxy
 * down — as opposed to the server answering with a rejection.
 *
 * The distinction matters for the stored session: a 401 means the token is
 * dead, but a request that never left the device says nothing about it. Callers
 * must not sign the user out on this.
 */
export function isNetworkError(error) {
  return error?.code === NETWORK_ERROR;
}

function dispatchAuthLogout(reason = "authentication_required") {
  window.dispatchEvent(new CustomEvent("auth:logout", { detail: { reason } }));
}

function clearOtherTokenCopies(activeStorageType = null) {
  for (const storageType of AUTH_STORAGE_TYPES) {
    if (storageType === activeStorageType) continue;
    localStorageService.removeRaw(AUTH_TOKEN_KEY, storageType);
  }
}

/**
 * Get the stored JWT token
 */
export function getToken() {
  for (const storageType of AUTH_STORAGE_TYPES) {
    const result = localStorageService.getRaw(AUTH_TOKEN_KEY, storageType);

    if (!result.ok) {
      continue;
    }

    if (result.value) {
      return result.value;
    }
  }

  return null;
}

/**
 * Set the JWT token
 */
export function setToken(token) {
  if (!token) {
    const failures = AUTH_STORAGE_TYPES.map((storageType) =>
      localStorageService.removeRaw(AUTH_TOKEN_KEY, storageType),
    ).filter((result) => !result.ok);

    return {
      ok: failures.length === 0,
      storageType: null,
      error: failures[0]?.error || null,
    };
  }

  for (const storageType of AUTH_STORAGE_TYPES) {
    const result = localStorageService.setRaw(
      AUTH_TOKEN_KEY,
      token,
      storageType,
    );

    if (result.ok) {
      clearOtherTokenCopies(storageType);
      return {
        ok: true,
        storageType,
        error: null,
      };
    }
  }

  return {
    ok: false,
    storageType: null,
    error: new Error("Browser storage is unavailable"),
  };
}

/**
 * Make an authenticated API request
 */
async function apiRequest(endpoint, options = {}) {
  const token = getToken();
  const headers = {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };
  const usedAuthToken = Boolean(headers.Authorization);

  let response;
  try {
    response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    });
  } catch (cause) {
    // fetch() rejects (rather than resolving with a status) only when the
    // request never reached the server. Tag it so callers can tell "no signal"
    // apart from "you are not who you say you are".
    const error = new Error("Khronos could not reach the server.");
    error.code = NETWORK_ERROR;
    error.cause = cause;
    throw error;
  }

  if (response.status === 401 || response.status === 403) {
    // Token expired or invalid
    if (usedAuthToken) {
      setToken(null);
      dispatchAuthLogout(
        response.status === 401 ? "missing_or_expired_token" : "invalid_token",
      );
    }
    throw new Error("Authentication required");
  }

  if (!response.ok) {
    const body = await response
      .json()
      .catch(() => ({ error: "Request failed" }));
    const error = new Error(body.error || "Request failed");
    // Machine-readable reason when the API gives one (e.g. APP_UPDATE_REQUIRED).
    error.status = response.status;
    if (body.code) error.code = body.code;
    throw error;
  }

  return response.json();
}

// ─── Auth ─────────────────────────────────────────────────

export async function loginWithGoogle(idToken) {
  const response = await apiRequest("/auth/google", {
    method: "POST",
    body: JSON.stringify({ idToken }),
  });

  if (response.token) {
    const tokenResult = setToken(response.token);

    if (!tokenResult.ok) {
      throw new Error(
        "Google sign-in succeeded, but Khronos could not store your sign-in on this device.",
      );
    }
  }

  return response;
}

export async function refreshToken() {
  return apiRequest("/auth/refresh", {
    method: "POST",
  });
}

export async function getCurrentUser() {
  return apiRequest("/auth/me");
}

export async function updateCurrentUser(userData) {
  return apiRequest("/auth/me", {
    method: "PUT",
    body: JSON.stringify(userData),
  });
}

export function logout() {
  return setToken(null);
}

// ─── Profile ─────────────────────────────────────────────

export async function getProfile() {
  return apiRequest("/profile");
}

export async function updateProfile(profileData) {
  return apiRequest("/profile", {
    method: "PUT",
    body: JSON.stringify(profileData),
  });
}

export async function syncProfile(localProfile) {
  return apiRequest("/profile/sync", {
    method: "POST",
    body: JSON.stringify({ profile: localProfile }),
  });
}

// ─── Sessions ────────────────────────────────────────────

export async function getSessions(options = {}) {
  const params = new URLSearchParams();
  if (options.limit) params.append("limit", options.limit);
  if (options.offset) params.append("offset", options.offset);
  // Narrowing happens server-side: the history is paged, so filtering what the
  // client already holds would only ever search the page it is looking at.
  if (options.q) params.append("q", options.q);
  if (options.from) params.append("from", options.from);
  if (options.to) params.append("to", options.to);

  const query = params.toString() ? `?${params.toString()}` : "";
  return apiRequest(`/sessions${query}`);
}

// Totals for the history stat tiles, over the same filter the list is showing.
// Separate from the list because the list is a page and the tiles are not —
// summing the fetched rows would report the page size, not the answer.
// `dates` comes back unfiltered, for the all-time streak.
export async function getSessionStats(options = {}) {
  const params = new URLSearchParams();
  if (options.q) params.append("q", options.q);
  if (options.from) params.append("from", options.from);
  if (options.to) params.append("to", options.to);

  const query = params.toString() ? `?${params.toString()}` : "";
  return apiRequest(`/sessions/stats${query}`);
}

/** VO2max estimates saved with sessions (#194), oldest first. */
// Progress page analysis (#203): { sessions, pending }. Call again while
// `pending` > 0; the server analyses old sessions a batch at a time.
export async function getSessionAnalysis() {
  return apiRequest("/sessions/analysis");
}

export async function getVo2maxHistory() {
  return apiRequest("/sessions/vo2max");
}

export async function clearVo2max(sessionId) {
  return apiRequest(`/sessions/vo2max/${sessionId}`, { method: "DELETE" });
}

export async function markVo2maxAirBike(sessionId) {
  return apiRequest(`/sessions/vo2max/${sessionId}/air-bike`, { method: "POST" });
}

export async function createSession(sessionData) {
  const saved = await apiRequest("/sessions", {
    method: "POST",
    body: JSON.stringify(sessionData),
  });
  // Badges this session earns arrive as a sheet, not a toast (#204).
  noteSessionSaved();
  // A rare effort is said straight away (api/lib/rareEffort.js).
  showRareEffort(saved?.rare_effort);
  return saved;
}

// The list endpoint omits the `telemetry` JSONB to keep responses small;
// fetch the full session row (including the 1 Hz array) on demand for chart
// rendering in the session-detail dialog.
export async function getSession(sessionId) {
  return apiRequest(`/sessions/${sessionId}`);
}

export async function updateSession(sessionId, sessionData) {
  return apiRequest(`/sessions/${sessionId}`, {
    method: "PUT",
    body: JSON.stringify(sessionData),
  });
}

export async function deleteSession(sessionId) {
  return apiRequest(`/sessions/${sessionId}`, {
    method: "DELETE",
  });
}

export async function getFriendsLeaderboard() {
  return apiRequest("/friends/leaderboard");
}

// Host-only: delete the server-fallback history row a live/solo session
// auto-recorded at end (the "Discard" choice on the completion screen).
export async function discardLiveHistory(liveSessionId) {
  return apiRequest(`/live/${liveSessionId}/history`, {
    method: "DELETE",
  });
}

export async function syncSessions(localSessions) {
  return apiRequest("/sessions/sync", {
    method: "POST",
    body: JSON.stringify({ sessions: localSessions }),
  });
}

// ─── Presets ──────────────────────────────────────────────

export async function getEquipmentPresets() {
  return apiRequest("/presets/equipment");
}

export async function createEquipmentPreset(presetData) {
  return apiRequest("/presets/equipment", {
    method: "POST",
    body: JSON.stringify(presetData),
  });
}

export async function updateEquipmentPreset(presetId, presetData) {
  return apiRequest(`/presets/equipment/${presetId}`, {
    method: "PUT",
    body: JSON.stringify(presetData),
  });
}

export async function deleteEquipmentPreset(presetId) {
  return apiRequest(`/presets/equipment/${presetId}`, {
    method: "DELETE",
  });
}

export async function getLocationPresets() {
  return apiRequest("/presets/locations");
}

export async function createLocationPreset(presetData) {
  return apiRequest("/presets/locations", {
    method: "POST",
    body: JSON.stringify(presetData),
  });
}

export async function updateLocationPreset(presetId, presetData) {
  return apiRequest(`/presets/locations/${presetId}`, {
    method: "PUT",
    body: JSON.stringify(presetData),
  });
}

export async function deleteLocationPreset(presetId) {
  return apiRequest(`/presets/locations/${presetId}`, {
    method: "DELETE",
  });
}

// ─── Performance Capabilities ───────────────────────────────

export async function getPerformanceCapabilities() {
  return apiRequest("/presets/capabilities");
}

export async function createPerformanceCapability(data) {
  return apiRequest("/presets/capabilities", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updatePerformanceCapability(id, data) {
  return apiRequest(`/presets/capabilities/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deletePerformanceCapability(id) {
  return apiRequest(`/presets/capabilities/${id}`, {
    method: "DELETE",
  });
}

// ─── Timer Presets ──────────────────────────────────────────

export async function getTimerPresets() {
  return apiRequest("/presets/timers");
}

export async function createTimerPreset(presetData) {
  return apiRequest("/presets/timers", {
    method: "POST",
    body: JSON.stringify(presetData),
  });
}

export async function updateTimerPreset(presetId, presetData) {
  return apiRequest(`/presets/timers/${presetId}`, {
    method: "PUT",
    body: JSON.stringify(presetData),
  });
}

export async function deleteTimerPreset(presetId) {
  return apiRequest(`/presets/timers/${presetId}`, {
    method: "DELETE",
  });
}

export async function touchTimerPreset(presetId) {
  return apiRequest(`/presets/timers/${presetId}/touch`, {
    method: "PATCH",
  });
}

// ─── Session Recipes (issue #78) ────────────────────────────

export async function getSessionRecipes() {
  return apiRequest("/presets/sessions");
}

export async function createSessionRecipe(recipeData) {
  return apiRequest("/presets/sessions", {
    method: "POST",
    body: JSON.stringify(recipeData),
  });
}

export async function updateSessionRecipe(recipeId, recipeData) {
  return apiRequest(`/presets/sessions/${recipeId}`, {
    method: "PUT",
    body: JSON.stringify(recipeData),
  });
}

export async function deleteSessionRecipe(recipeId) {
  return apiRequest(`/presets/sessions/${recipeId}`, {
    method: "DELETE",
  });
}

export async function touchSessionRecipe(recipeId) {
  return apiRequest(`/presets/sessions/${recipeId}/touch`, {
    method: "PATCH",
  });
}

// --- Settings ---

// ─── Exchange rate ────────────────────────────────────────

/** Today's US$ → A$ rate: `{ usdAud: { rate, date, source } | null }`. */
export async function getFxRate() {
  return apiRequest("/fx");
}

// ─── Personal AI usage ────────────────────────────────────

/** Record one AI request the app made (see apis/llm/usageLog.js). */
export async function recordAiUsage(entry) {
  return apiRequest("/usage", { method: "POST", body: JSON.stringify(entry) });
}

/** The Usage sheet's report, in "AUD" or "USD". */
export async function getUsageReport(currency) {
  return apiRequest(`/usage/report?currency=${encodeURIComponent(currency || "AUD")}`);
}

/** Set (`{ amount, currency }`) or clear (`{ amount: null }`) the monthly budget. */
export async function setUsageBudget(budget) {
  return apiRequest("/usage/budget", { method: "PUT", body: JSON.stringify(budget) });
}

// ─── Shared AI credits ────────────────────────────────────

export async function getCreditShares() {
  return apiRequest("/credit-shares");
}

export async function createCreditShare(share) {
  return apiRequest("/credit-shares", {
    method: "POST",
    body: JSON.stringify(share),
  });
}

export async function updateCreditShare(shareId, changes) {
  return apiRequest(`/credit-shares/${shareId}`, {
    method: "PATCH",
    body: JSON.stringify(changes),
  });
}

/** The latest calls on a share: `[{ at, model, inputTokens, outputTokens, cost, exact }]`. */
export async function getCreditShareUsage(shareId) {
  return apiRequest(`/credit-shares/${shareId}/usage`);
}

export async function revokeCreditShare(shareId) {
  return apiRequest(`/credit-shares/${shareId}/revoke`, { method: "POST" });
}

// Challenges (lib/challenges.js on the API has the rules). Every call
// answers with the lot: { challenges, mates }.
export async function getChallenges() {
  return apiRequest("/challenges");
}

/** Join a Khronos challenge (by key), or accept a mate's (by number). */
export async function joinChallenge(id) {
  return apiRequest(`/challenges/${encodeURIComponent(id)}/join`, { method: "POST" });
}

/** Leave a Khronos challenge, or decline, cancel or stop a mate's. */
export async function leaveChallenge(id) {
  return apiRequest(`/challenges/${encodeURIComponent(id)}/leave`, { method: "POST" });
}

export async function challengeMate(userId, weeks) {
  return apiRequest("/challenges", { method: "POST", body: JSON.stringify({ userId, weeks }) });
}

// The mates' weekly board (lib/board.js on the API): this week unless a
// past Monday ("YYYY-MM-DD") is given.
export async function getMatesBoard(week) {
  return apiRequest(`/mates/board${week ? `?week=${encodeURIComponent(week)}` : ""}`);
}

// Training partners (lib/partners.js on the API has the rules).
export async function getPartners() {
  return apiRequest("/partners");
}

export async function askPartner(userId) {
  return apiRequest("/partners", { method: "POST", body: JSON.stringify({ userId }) });
}

export async function acceptPartner(id) {
  return apiRequest(`/partners/${id}/accept`, { method: "POST" });
}

/** End a partnership, or decline or cancel a request. */
export async function endPartner(id) {
  return apiRequest(`/partners/${id}/end`, { method: "POST" });
}

/** Ask the owner for more: `{ asked, already }` (already = asked this round). */
export async function askForMoreCredits(shareId) {
  return apiRequest(`/credit-shares/${shareId}/ask-more`, { method: "POST" });
}

export async function getSettings() {
  return apiRequest("/settings");
}

export async function updateSettings(settingsData) {
  return apiRequest("/settings", {
    method: "PUT",
    body: JSON.stringify(settingsData),
  });
}

// ─── Generation diagnostics ─────────────────────────────

/**
 * Record which deterministic repairs fired and which validation issues
 * survived for one session generation. Fire-and-forget: a failure here must
 * never surface to the user, since nothing about the session depends on it.
 */
export async function recordGenerationDiagnostics(payload) {
  return apiRequest("/diagnostics/generation", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** Per-code totals over a window, newest-first. `days` defaults to 90. */
export async function getGenerationDiagnostics(days) {
  const query = Number.isFinite(Number(days)) ? `?days=${Number(days)}` : "";
  return apiRequest(`/diagnostics/generation${query}`);
}

// ─── Programmes ─────────────────────────────────────────

export async function getActiveProgramme() {
  return apiRequest("/programmes/active");
}

// #58: library list of all the user's programmes (active pinned first).
export async function getProgrammes() {
  return apiRequest("/programmes");
}

// #58: full detail of one programme (open/preview without switching).
export async function getProgrammeDetail(programmeId) {
  return apiRequest(`/programmes/${programmeId}`);
}

// #58: make a programme the active one (others → paused), from `firstDay`.
export async function activateProgramme(programmeId, firstDay) {
  return apiRequest(`/programmes/${programmeId}/activate`, {
    method: "PUT",
    // #125: the day week 1 starts again; the server uses today without it.
    body: JSON.stringify(firstDay ? { first_day: firstDay } : {}),
  });
}

// #58: update programme metadata; structural changes regenerate the skeleton.
export async function updateProgramme(programmeId, data) {
  return apiRequest(`/programmes/${programmeId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function createProgramme(data) {
  return apiRequest("/programmes", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function assignProgrammeSession(programmeId, sessionId, data) {
  return apiRequest(`/programmes/${programmeId}/sessions/${sessionId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// #184: training a programme together.
/** The invite link's code for training this programme together. */
export async function createProgrammeInvite(programmeId) {
  return apiRequest(`/programmes/${programmeId}/together`, { method: "POST" });
}

/** The mates on a programme, or null when it isn't shared. */
export async function getProgrammeMates(programmeId) {
  return apiRequest(`/programmes/${programmeId}/together`);
}

export async function getProgrammeInvite(code) {
  return apiRequest(`/programmes/together/${encodeURIComponent(code)}`);
}

export async function joinProgrammeInvite(code) {
  return apiRequest(`/programmes/together/${encodeURIComponent(code)}/join`, { method: "POST" });
}

export async function completeProgrammeSession(programmeId, sessionId, data) {
  return apiRequest(
    `/programmes/${programmeId}/sessions/${sessionId}/complete`,
    {
      method: "PUT",
      body: JSON.stringify(data),
    },
  );
}

export async function missProgrammeSession(programmeId, sessionId) {
  return apiRequest(`/programmes/${programmeId}/sessions/${sessionId}/miss`, {
    method: "PUT",
  });
}

// #59: clear an assigned session back to its empty skeleton slot.
export async function clearProgrammeSession(programmeId, sessionId) {
  return apiRequest(`/programmes/${programmeId}/sessions/${sessionId}`, {
    method: "DELETE",
  });
}

// #59: reorder one week's sessions. `order` is the week's session ids in the
// desired order. Returns the refreshed programme.
export async function reorderProgrammeWeek(programmeId, weekNumber, order) {
  return apiRequest(`/programmes/${programmeId}/weeks/${weekNumber}/reorder`, {
    method: "PUT",
    body: JSON.stringify({ order }),
  });
}

// #196 week pool: put a session on a day of its week (0 Mon - 6 Sun), or
// into the week's pool with null. Returns the refreshed programme.
export async function placeProgrammeSession(programmeId, sessionId, weekday) {
  return apiRequest(`/programmes/${programmeId}/sessions/${sessionId}/place`, {
    method: "PUT",
    body: JSON.stringify({ weekday: weekday ?? null }),
  });
}

// #196: count a session already done as this programme session.
export async function linkProgrammeSession(programmeId, sessionId, workoutSessionId) {
  return apiRequest(`/programmes/${programmeId}/sessions/${sessionId}/link`, {
    method: "PUT",
    body: JSON.stringify({ workout_session_id: workoutSessionId }),
  });
}

// #196: skip a session this week. Returns the refreshed programme.
export async function skipProgrammeSession(programmeId, sessionId) {
  return apiRequest(`/programmes/${programmeId}/sessions/${sessionId}/skip`, { method: "PUT" });
}

// #196: the equipment set a programme's sessions are built for; null is the
// Profile default. Returns the refreshed programme.
export async function setProgrammeEquipment(programmeId, equipmentPresetId) {
  return apiRequest(`/programmes/${programmeId}/equipment`, {
    method: "PUT",
    body: JSON.stringify({ equipment_preset_id: equipmentPresetId ?? null }),
  });
}

// #59: reset one week back to the AI skeleton. Returns the refreshed programme.
// #92: `slots` is the app's re-planned week (config/programmePlanner.js).
export async function resetProgrammeWeek(programmeId, weekNumber, slots) {
  return apiRequest(`/programmes/${programmeId}/weeks/${weekNumber}/reset`, {
    method: "POST",
    body: JSON.stringify({ slots }),
  });
}

// #94: move deload weeks; `slots` re-plans every week whose status changes.
export async function setProgrammeDeloads(programmeId, deloadWeeks, slots) {
  return apiRequest(`/programmes/${programmeId}/deloads`, {
    method: "PUT",
    body: JSON.stringify({ deload_weeks: deloadWeeks, slots }),
  });
}

// #92: move the programme a week later/earlier, reopening or re-missing days.
export async function shiftProgramme(programmeId, { weeks, reopen = [], miss = [] }) {
  return apiRequest(`/programmes/${programmeId}/shift`, {
    method: "PUT",
    body: JSON.stringify({ weeks, reopen, miss }),
  });
}

// #94: the user's answer about a finished week.
export async function saveProgrammeCheckin(programmeId, weekNumber, { answer, action }) {
  return apiRequest(`/programmes/${programmeId}/checkins/${weekNumber}`, {
    method: "PUT",
    body: JSON.stringify({ answer, action }),
  });
}

export async function deleteProgrammeCheckin(programmeId, weekNumber) {
  return apiRequest(`/programmes/${programmeId}/checkins/${weekNumber}`, {
    method: "DELETE",
  });
}

export async function deleteProgramme(programmeId) {
  return apiRequest(`/programmes/${programmeId}`, {
    method: "DELETE",
  });
}

// ─── Devices ────────────────────────────────────────────

export async function getDevices() {
  return apiRequest("/devices");
}

export async function linkDevice(code) {
  return apiRequest("/devices/link", {
    method: "POST",
    body: JSON.stringify({ code }),
  });
}

export async function unlinkDevice(deviceId) {
  return apiRequest(`/devices/${deviceId}/unlink`, {
    method: "DELETE",
  });
}

// Sharing a timer with mates (#171).
export async function getSharedDevices() {
  return apiRequest("/devices/shared");
}

export async function getDeviceShares(deviceId) {
  return apiRequest(`/devices/${deviceId}/shares`);
}

export async function shareDevice(deviceId, userId, expiresAt = null) {
  return apiRequest(`/devices/${deviceId}/shares`, {
    method: "POST",
    body: JSON.stringify({ user_id: userId, expires_at: expiresAt }),
  });
}

export async function stopDeviceShare(shareId) {
  return apiRequest(`/devices/shares/${shareId}`, { method: "DELETE" });
}

export async function renameDevice(deviceId, name) {
  return apiRequest(`/devices/${deviceId}`, {
    method: "PATCH",
    body: JSON.stringify({ name }),
  });
}

export async function pushConfigToDevice(deviceId, plan, sessionRef) {
  return apiRequest(`/devices/${deviceId}/config`, {
    method: "POST",
    body: JSON.stringify({ plan, session_ref: sessionRef }),
  });
}

export async function getDeviceLive(deviceId) {
  return apiRequest(`/devices/${deviceId}/live`);
}

export async function sendBleCommand(deviceId, command) {
  return apiRequest(`/devices/${deviceId}/ble-command`, {
    method: "POST",
    body: JSON.stringify({ command }),
  });
}

export async function sendControlCommand(deviceId, command) {
  return apiRequest(`/devices/${deviceId}/control`, {
    method: "POST",
    body: JSON.stringify({ command }),
  });
}

// ─── Firmware ────────────────────────────────────────────

export async function getFirmwareReleases() {
  return apiRequest("/firmware/releases");
}

export async function requestFirmwareInstall(deviceId) {
  return apiRequest(`/devices/${deviceId}/update`, { method: "POST" });
}

export async function cancelFirmwareInstall(deviceId) {
  return apiRequest(`/devices/${deviceId}/update`, { method: "DELETE" });
}

// ─── Equipment (BLE: HR straps, FTMS cardio) ────────────

export async function getUserEquipment() {
  return apiRequest("/equipment");
}

export async function saveUserEquipment(equipment) {
  return apiRequest("/equipment", {
    method: "PUT",
    body: JSON.stringify(equipment),
  });
}

export async function setUserEquipmentLabel({ kind, equipment_type, name, label }) {
  return apiRequest("/equipment/label", {
    method: "PATCH",
    body: JSON.stringify({ kind, equipment_type, name, label }),
  });
}

export async function deleteUserEquipment(rowId) {
  return apiRequest(`/equipment/${rowId}`, {
    method: "DELETE",
  });
}

// ─── Friends ─────────────────────────────────────────────

export async function getFriends() {
  return apiRequest("/friends");
}

export async function getFriendRequests() {
  return apiRequest("/friends/requests");
}

export async function getSentFriendRequests() {
  return apiRequest("/friends/sent");
}

export async function sendFriendRequest(addresseeId) {
  return apiRequest("/friends/request", {
    method: "POST",
    body: JSON.stringify({ addressee_id: addresseeId }),
  });
}

export async function acceptFriendRequest(friendshipId) {
  return apiRequest(`/friends/${friendshipId}/accept`, { method: "PUT" });
}

export async function rejectFriendRequest(friendshipId) {
  return apiRequest(`/friends/${friendshipId}/reject`, { method: "PUT" });
}

export async function blockUser(friendshipId) {
  return apiRequest(`/friends/${friendshipId}/block`, { method: "PUT" });
}

export async function removeFriend(friendshipId) {
  return apiRequest(`/friends/${friendshipId}`, { method: "DELETE" });
}

export async function searchUsers(query) {
  return apiRequest(`/friends/search?q=${encodeURIComponent(query)}`);
}

export async function getFriendStreaks() {
  return apiRequest("/friends/streaks");
}

// ─── Groups ──────────────────────────────────────────────

export async function getGroups() {
  return apiRequest("/groups");
}

export async function createGroupApi(data) {
  return apiRequest("/groups", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getGroup(groupId) {
  return apiRequest(`/groups/${groupId}`);
}

export async function updateGroupApi(groupId, data) {
  return apiRequest(`/groups/${groupId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteGroupApi(groupId) {
  return apiRequest(`/groups/${groupId}`, { method: "DELETE" });
}

export async function joinGroupApi(groupId) {
  return apiRequest(`/groups/${groupId}/join`, { method: "POST" });
}

export async function leaveGroupApi(groupId) {
  return apiRequest(`/groups/${groupId}/leave`, { method: "POST" });
}

export async function inviteToGroup(groupId, userId) {
  return apiRequest(`/groups/${groupId}/invite`, {
    method: "POST",
    body: JSON.stringify({ user_id: userId }),
  });
}

export async function updateMemberRole(groupId, userId, role) {
  return apiRequest(`/groups/${groupId}/members/${userId}/role`, {
    method: "PUT",
    body: JSON.stringify({ role }),
  });
}

export async function removeGroupMember(groupId, userId) {
  return apiRequest(`/groups/${groupId}/members/${userId}`, {
    method: "DELETE",
  });
}

// ─── Feed ────────────────────────────────────────────────

export async function getFeed(options = {}) {
  const params = new URLSearchParams();
  if (options.limit) params.append("limit", options.limit);
  if (options.before) params.append("before", options.before);
  const query = params.toString() ? `?${params.toString()}` : "";
  return apiRequest(`/feed${query}`);
}

export async function getGroupFeed(groupId, options = {}) {
  const params = new URLSearchParams();
  if (options.limit) params.append("limit", options.limit);
  if (options.before) params.append("before", options.before);
  const query = params.toString() ? `?${params.toString()}` : "";
  return apiRequest(`/feed/group/${groupId}${query}`);
}

export async function createPost(data) {
  return apiRequest("/feed", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function deletePostApi(postId) {
  return apiRequest(`/feed/${postId}`, { method: "DELETE" });
}

// ─── Notifications ───────────────────────────────────────

export async function getNotifications(limit) {
  const query = limit ? `?limit=${limit}` : "";
  return apiRequest(`/notifications${query}`);
}

/** Mark every notification read, or only `ids` when given. */
export async function markNotificationsRead(ids) {
  return apiRequest("/notifications/read", {
    method: "POST",
    body: JSON.stringify(ids ? { ids } : {}),
  });
}

/** Every badge for you ("me") or a mate, earned or not (#153). */
export async function getBadges(userId = "me") {
  return apiRequest(`/badges/${userId}`);
}

export async function getComments(postId) {
  return apiRequest(`/feed/${postId}/comments`);
}

export async function addComment(postId, content) {
  return apiRequest(`/feed/${postId}/comments`, {
    method: "POST",
    body: JSON.stringify({ content }),
  });
}

export async function updateComment(postId, commentId, content) {
  return apiRequest(`/feed/${postId}/comments/${commentId}`, {
    method: "PUT",
    body: JSON.stringify({ content }),
  });
}

export async function deleteCommentApi(postId, commentId) {
  return apiRequest(`/feed/${postId}/comments/${commentId}`, {
    method: "DELETE",
  });
}

export async function toggleLike(postId) {
  return apiRequest(`/feed/${postId}/like`, { method: "POST" });
}

export async function copyWorkout(postId) {
  return apiRequest(`/feed/${postId}/copy`, { method: "POST" });
}

// ─── Trained together ────────────────────────────────────

/**
 * Claim that you and `partnerId` did the same workout.
 *
 * From your own post pass `sessionId`; from a mate's post pass
 * `partnerSessionId` and let the server match your side by time. The claim is
 * pending until they confirm.
 */
export async function linkSessions({ partnerId, sessionId, partnerSessionId, mode }) {
  return apiRequest("/session-links", {
    method: "POST",
    body: JSON.stringify({
      partner_id: partnerId,
      session_id: sessionId ?? null,
      partner_session_id: partnerSessionId ?? null,
      mode: mode || "in_person",
    }),
  });
}

export async function getPendingSessionLinks() {
  return apiRequest("/session-links/pending");
}

export async function respondToSessionLink(linkId, accept) {
  return apiRequest(`/session-links/${linkId}/respond`, {
    method: "POST",
    body: JSON.stringify({ accept }),
  });
}

export async function unlinkSessions(linkId) {
  return apiRequest(`/session-links/${linkId}`, { method: "DELETE" });
}

// ─── Live Sessions ───────────────────────────────────────

export async function createLiveSession(data) {
  return apiRequest("/live", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getActiveLiveSessions() {
  return apiRequest("/live/active");
}

export async function getMyLiveSession() {
  return apiRequest("/live/mine");
}

export async function getLiveSessionInfo(joinCode) {
  return apiRequest(`/live/${joinCode}`);
}

export async function joinLiveSessionApi(joinCode) {
  return apiRequest(`/live/${joinCode}/join`, { method: "POST" });
}

/**
 * Read-only view of a live session, for someone holding a watch link. The one
 * live endpoint that answers without a JWT — the watch code is the credential
 * — so a signed-out visitor can follow along without an account.
 */
export async function getWatchSession(watchCode) {
  return apiRequest(`/live/watch/${encodeURIComponent(watchCode)}`);
}

export async function endLiveSession(sessionId) {
  return apiRequest(`/live/${sessionId}/end`, { method: "POST" });
}

// ─── Shares & Invites ────────────────────────────────────
//
// The two GET-by-code calls are the only endpoints in this client that are
// meant to work signed out: apiRequest simply omits the Authorization header
// when there is no token, and the server answers a link preview either way.

export async function createShare(data) {
  return apiRequest("/shares", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getShares() {
  return apiRequest("/shares");
}

export async function getShare(code) {
  return apiRequest(`/shares/${encodeURIComponent(code)}`);
}

export async function claimShare(code) {
  return apiRequest(`/shares/${encodeURIComponent(code)}/use`, {
    method: "POST",
  });
}

export async function revokeShare(code) {
  return apiRequest(`/shares/${encodeURIComponent(code)}`, {
    method: "DELETE",
  });
}

export async function createInvite(data = {}) {
  return apiRequest("/invites", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getInvites() {
  return apiRequest("/invites");
}

export async function getInvite(code) {
  return apiRequest(`/invites/${encodeURIComponent(code)}`);
}

export async function acceptInvite(code) {
  return apiRequest(`/invites/${encodeURIComponent(code)}/accept`, {
    method: "POST",
  });
}

export async function revokeInvite(code) {
  return apiRequest(`/invites/${encodeURIComponent(code)}`, {
    method: "DELETE",
  });
}

export default {
  // Auth
  loginWithGoogle,
  refreshToken,
  getCurrentUser,
  updateCurrentUser,
  logout,
  getToken,
  setToken,
  // Profile
  getProfile,
  updateProfile,
  syncProfile,
  // Sessions
  getSessions,
  getSessionStats,
  getVo2maxHistory,
  getSessionAnalysis,
  clearVo2max,
  markVo2maxAirBike,
  getSession,
  createSession,
  updateSession,
  deleteSession,
  discardLiveHistory,
  syncSessions,
  // Settings
  getSettings,
  updateSettings,
  recordGenerationDiagnostics,
  getGenerationDiagnostics,
  // Presets
  getEquipmentPresets,
  createEquipmentPreset,
  updateEquipmentPreset,
  deleteEquipmentPreset,
  getLocationPresets,
  createLocationPreset,
  updateLocationPreset,
  deleteLocationPreset,
  // Programmes
  getActiveProgramme,
  getProgrammes,
  getProgrammeDetail,
  activateProgramme,
  updateProgramme,
  createProgramme,
  assignProgrammeSession,
  completeProgrammeSession,
  missProgrammeSession,
  clearProgrammeSession,
  reorderProgrammeWeek,
  setProgrammeEquipment,
  placeProgrammeSession,
  skipProgrammeSession,
  linkProgrammeSession,
  resetProgrammeWeek,
  deleteProgramme,
  // Devices
  getDevices,
  getUserEquipment,
  saveUserEquipment,
  setUserEquipmentLabel,
  deleteUserEquipment,
  linkDevice,
  unlinkDevice,
  renameDevice,
  pushConfigToDevice,
  getSharedDevices,
  getDeviceShares,
  shareDevice,
  stopDeviceShare,
  getDeviceLive,
  sendBleCommand,
  // Friends
  getFriends,
  getFriendRequests,
  getSentFriendRequests,
  sendFriendRequest,
  acceptFriendRequest,
  rejectFriendRequest,
  blockUser,
  removeFriend,
  searchUsers,
  getFriendStreaks,
  // Groups
  getGroups,
  createGroupApi,
  getGroup,
  updateGroupApi,
  deleteGroupApi,
  joinGroupApi,
  leaveGroupApi,
  inviteToGroup,
  updateMemberRole,
  removeGroupMember,
  // Feed
  getFeed,
  getGroupFeed,
  createPost,
  deletePostApi,
  getNotifications,
  markNotificationsRead,
  getBadges,
  getComments,
  addComment,
  updateComment,
  deleteCommentApi,
  toggleLike,
  copyWorkout,
  linkSessions,
  getPendingSessionLinks,
  respondToSessionLink,
  unlinkSessions,
  // Live Sessions
  createLiveSession,
  getActiveLiveSessions,
  getMyLiveSession,
  getLiveSessionInfo,
  joinLiveSessionApi,
  getWatchSession,
  endLiveSession,
  // Shares & Invites
  createShare,
  getShares,
  getShare,
  claimShare,
  revokeShare,
  createInvite,
  getInvites,
  getInvite,
  acceptInvite,
  revokeInvite,
  getFxRate,
  recordAiUsage,
  getUsageReport,
  setUsageBudget,
  // Shared AI credits
  getCreditShares,
  createCreditShare,
  updateCreditShare,
  getCreditShareUsage,
  revokeCreditShare,
  askForMoreCredits,
  getPartners,
  askPartner,
  acceptPartner,
  endPartner,
};

// ─── Connected apps: Nexus ────────────────────────────────

export async function getNexusLink() {
  return apiRequest("/integrations/nexus");
}

export async function linkNexus(nexus, code) {
  return apiRequest("/integrations/nexus/link", { method: "POST", body: JSON.stringify({ nexus, code }) });
}

export async function syncNexus() {
  return apiRequest("/integrations/nexus/sync", { method: "POST" });
}

export async function unlinkNexus() {
  return apiRequest("/integrations/nexus", { method: "DELETE" });
}
